/**
 * WebSocket Types for Frontend
 * 
 * Defines types and interfaces for WebSocket communication on the client side.
 */

// ⭐ 从 @packages/global 导入统一的 TaskType 枚举
import { TaskType } from '@packages/global';

// 重新导出 TaskType，保持向后兼容
export { TaskType };

/**
 * Task status enum
 */
export enum TaskStatus {
  PENDING = 'pending',
  SUBMITTED = 'submitted',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed'
}

/**
 * Message types for WebSocket communication
 */
export enum MessageType {
  TASK_CREATED = 'task:created',
  TASK_STATUS_CHANGED = 'task:status_changed',
  TASK_PROGRESS = 'task:progress',
  TASK_COMPLETED = 'task:completed',
  TASK_FAILED = 'task:failed',
  BATCH_PROGRESS = 'batch:progress',
  BATCH_COMPLETED = 'batch:completed',
  COIN_BALANCE_CHANGED = 'coin:balance_changed',
  ERROR = 'error',
  HEARTBEAT_PING = 'heartbeat:ping',
  HEARTBEAT_PONG = 'heartbeat:pong',
  // Script generation message types
  SCRIPT_STATUS_CHANGED = 'script:status_changed',
  SCRIPT_STEP_PROGRESS = 'script:step_progress',
  SCRIPT_SHOTS_GENERATED = 'script:shots_generated',
  SCRIPT_STEP1_STORYLINE = 'script:step1_storyline',
  EPISODE_TITLE_UPDATED = 'episode:title_updated',
  // Script preparation progress message types
  PREPARATION_PROGRESS = 'preparation:progress',
  PREPARATION_STORY_INFO = 'preparation:story_info',
  PREPARATION_COMPLETED = 'preparation:completed',
  PREPARATION_FAILED = 'preparation:failed',
  // Project setting auto-generation message types
  PROJECT_SETTING_STEP_STARTED = 'project_setting:step_started',
  PROJECT_SETTING_STEP_PROGRESS = 'project_setting:step_progress',
  PROJECT_SETTING_STEP_COMPLETED = 'project_setting:step_completed',
  PROJECT_SETTING_STEP_FAILED = 'project_setting:step_failed',
  PROJECT_SETTING_PROJECT_META_UPDATED = 'project_setting:project_meta_updated',
  PROJECT_SETTING_EPISODE_SCRIPT_PROGRESS = 'project_setting:episode_script_progress',
  // MV 生成相关消息类型
  MV_ANALYSIS_PROGRESS = 'mv:analysis_progress',
  MV_ANALYSIS_COMPLETED = 'mv:analysis_completed',
  MV_ANALYSIS_FAILED = 'mv:analysis_failed',
}

/**
 * Connection status
 */
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  FAILED = 'failed'
}

/**
 * Subscription types
 */
export type SubscriptionType = 'task' | 'taskType' | 'batch' | 'episode' | 'project';

/**
 * Subscription interface
 */
export interface Subscription {
  type: SubscriptionType;
  taskId?: string;
  taskType?: TaskType;
  batchId?: string;
  episodeId?: string;
  projectId?: string;
}

/**
 * WebSocket Client Configuration
 */
export interface ClientConfig {
  serverUrl: string;
  authToken: string;
  reconnectAttempts: number;
  reconnectDelay: number;
  reconnectDelayMax: number;
  heartbeatTimeout: number;
}

/**
 * Task status changed message
 */
export interface TaskStatusChangedMessage {
  messageType: 'task:status_changed';
  taskId: string;
  taskType: TaskType;
  oldStatus: TaskStatus;
  newStatus: TaskStatus;
  userId: string;
  timestamp: string;
  data?: {
    result?: any;
    error?: string;
    metadata?: Record<string, any>;
  };
}

/**
 * Task progress message
 */
export interface TaskProgressMessage {
  messageType: 'task:progress';
  taskId: string;
  taskType: TaskType;
  progress: number;
  currentStep: string;
  estimatedTimeRemaining: number;
  userId: string;
  timestamp: string;
}

/**
 * Batch progress message
 */
export interface BatchProgressMessage {
  messageType: 'batch:progress';
  batchId: string;
  totalTasks: number;
  completedTasks: number;
  failedTasks: number;
  processingTasks: number;
  overallProgress: number;
  taskStatuses: Array<{
    taskId: string;
    status: TaskStatus;
    progress?: number;
  }>;
  userId: string;
  timestamp: string;
}

/**
 * Error message
 */
export interface ErrorMessage {
  messageType: 'error';
  errorCode: string;
  errorMessage: string;
  details?: any;
  timestamp: string;
}

/**
 * Subscription response message
 */
export interface SubscriptionResponseMessage {
  messageType: 'subscription:response';
  success: boolean;
  subscription: Subscription;
  error?: string;
  timestamp: string;
}

/**
 * Message handler type
 */
export type MessageHandler = (message: any) => void;

/**
 * Connection event handlers
 */
export interface ConnectionEventHandlers {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onReconnect?: (attemptNumber: number) => void;
  onReconnectFailed?: () => void;
  onError?: (error: Error) => void;
}

// Re-export types from @packages/global for WebSocket messages
import type {
  ScriptGenerationStatus,
  ScriptShot,
  GenerationStepStatus
} from '@packages/global';

export type { ScriptGenerationStatus };

/**
 * Script step status type
 */
export type ScriptStepStatus = 'pending' | 'processing' | 'completed' | 'failed';

/**
 * Script status changed message
 */
export interface ScriptStatusChangedMessage {
  messageType: 'script:status_changed';
  scriptId: string;
  /** Episode ID for episode-level WebSocket subscription routing */
  episodeId: string;
  oldStatus: ScriptGenerationStatus;
  newStatus: ScriptGenerationStatus;
  errorMessage?: string;
  userId: string;
  timestamp: string;
}

/**
 * Script step progress message
 */
export interface ScriptStepProgressMessage {
  messageType: 'script:step_progress';
  scriptId: string;
  /** Episode ID for episode-level WebSocket subscription routing */
  episodeId: string;
  stepNumber: number;
  stepName: string;
  status: ScriptStepStatus;
  progress: number; // 0-100
  subSteps?: {
    name: string;
    status: ScriptStepStatus;
    progress: number;
  }[];
  userId: string;
  timestamp: string;
}

/**
 * Script shots generated message
 * Uses ScriptShot from @packages/global
 */
export interface ScriptShotsGeneratedMessage {
  messageType: 'script:shots_generated';
  scriptId: string;
  /** Episode ID for episode-level WebSocket subscription routing */
  episodeId: string;
  newShots: ScriptShot[];
  startIndex: number;
  totalShotCount: number;
  isComplete: boolean;
  userId: string;
  timestamp: string;
}


/**
 * 准备进度消息
 * 当准备任务状态发生变化时广播（pending → processing → completed/failed）
 */
export interface PreparationProgressMessage {
  messageType: 'preparation:progress';
  jobId: string;
  userId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  currentStep: string;  // 当前步骤的友好描述
  scriptMode?: string;  // 识别出的剧本模式（使用 ScriptMode 枚举值）
  timestamp: string;
}

/**
 * 故事信息流式消息
 * 当 LLM 生成故事信息时，流式推送增量内容
 */
export interface PreparationStoryInfoMessage {
  messageType: 'preparation:story_info';
  jobId: string;
  userId: string;
  field: 'title' | 'summary' | 'welcomeMessage' | 'language';
  delta: string;        // 增量内容
  fullContent: string;  // 完整内容（用于断线重连）
  isComplete: boolean;  // 该字段是否已完成
  timestamp: string;
}

/**
 * 准备完成消息
 * 当准备任务成功完成时广播，包含创建的 scriptId 和 episodeId
 */
export interface PreparationCompletedMessage {
  messageType: 'preparation:completed';
  jobId: string;
  userId: string;
  scriptId: string;
  episodeId: string;
  timestamp: string;
}

/**
 * 准备失败消息
 * 当准备任务失败时广播，包含用户友好的错误信息
 */
export interface PreparationFailedMessage {
  messageType: 'preparation:failed';
  jobId: string;
  userId: string;
  error: string;
  timestamp: string;
}

/**
 * Step 1 故事线流式进度消息
 * 当 Step 1 生成故事线时，流式推送增量内容
 */
export interface ScriptStep1StorylineMessage {
  messageType: 'script:step1_storyline';
  scriptId: string;
  /** Episode ID for episode-level WebSocket subscription routing */
  episodeId: string;
  userId: string;
  /** 当前正在生成的字段 */
  field?: 'language' | 'title' | 'welcomeMessage' | 'summary' | 'highlights';
  /** 增量内容（流式输出） */
  delta?: string;
  /** 完整内容 */
  fullContent?: string;
  /** 该字段是否生成完成 */
  isComplete?: boolean;
  /** 剧本模式（使用 ScriptMode 枚举值） */
  scriptMode?: string;
  timestamp: string;
}

/**
 * Episode 标题更新消息
 * 当 Step 1 完成后，Episode 标题更新时广播
 */
export interface EpisodeTitleUpdatedMessage {
  messageType: 'episode:title_updated';
  scriptId: string;
  episodeId: string;
  userId: string;
  /** 新的标题 */
  title: string;
  timestamp: string;
}

// ============================================================================
// 项目设定自动生成相关消息类型（从 @packages/global 重新导出）
// ============================================================================

import {
  GenerationStepType,
  ProjectSettingStepStatus,
  type ProjectSettingStepStartedMessage,
  type ProjectSettingStepProgressMessage,
  type ProjectSettingStepCompletedMessage,
  type ProjectSettingStepFailedMessage,
  type ProjectMetaUpdatedMessage,
  type EpisodeScriptProgressMessage,
  type ProjectSettingGenerationMessage
} from '@packages/global';

export { GenerationStepType, ProjectSettingStepStatus };
export type {
  ProjectSettingStepStartedMessage,
  ProjectSettingStepProgressMessage,
  ProjectSettingStepCompletedMessage,
  ProjectSettingStepFailedMessage,
  ProjectMetaUpdatedMessage,
  EpisodeScriptProgressMessage,
  ProjectSettingGenerationMessage
};

// ============================================================================
// MV 生成相关消息类型
// ============================================================================

import type { AudioAnalysisResult } from '@packages/global';

export interface MvAnalysisProgressMessage {
  messageType: 'mv:analysis_progress';
  taskId: string;
  userId: string;
  projectId: string;
  progress: number;
  currentStep: string;
  timestamp: string;
}

export interface MvAnalysisCompletedMessage {
  messageType: 'mv:analysis_completed';
  taskId: string;
  userId: string;
  projectId: string;
  analysisResult: AudioAnalysisResult;
  segmentCount: number;
  totalDuration: number;
  timestamp: string;
}

export interface MvAnalysisFailedMessage {
  messageType: 'mv:analysis_failed';
  taskId: string;
  userId: string;
  projectId: string;
  error: string;
  timestamp: string;
}
